/**
 * The member area switch (docs/work-plan-member-area.md, phase 1).
 *
 * REACT_APP_MEMBER_AREA=on turns on the new top bar and bottom bar, the avatar
 * menu, the KYC page, banner and pop-up, the notifications bell and the new
 * listing rows. Off: everything looks and works as before. The server reads
 * the same value (server/api-util/memberArea.js).
 *
 * The bell reads deal events, so it is meant to be on together with the new
 * inbox (REACT_APP_NEW_INBOX).
 */
export const memberAreaEnabled = () => process.env.REACT_APP_MEMBER_AREA === 'on';

/**
 * Where the member stands with KYC, from their own profile:
 *   'approved'  - staff verified the passport (publicData.isVerified)
 *   'submitted' - a passport is uploaded and waits for staff ("submitted" and
 *                 "under review" are one state, decision D9)
 *   'none'      - nothing uploaded yet
 */
export const kycStateOf = user => {
  const profile = user?.attributes?.profile || {};
  if (profile.publicData?.isVerified) return 'approved';
  const privateData = profile.privateData || {};
  const hasFiles = Array.isArray(privateData.passportFiles) && privateData.passportFiles.length > 0;
  if (hasFiles || privateData.idorpassport || privateData.passportUploaded) return 'submitted';
  return 'none';
};

/** Whether the member has set their deal preferences (asset classes). */
export const hasPreferences = user => {
  const publicData = user?.attributes?.profile?.publicData || {};
  const focus = publicData.mainFocus || publicData.asasets_of_focus;
  return Array.isArray(focus) ? focus.length > 0 : !!focus;
};

/**
 * Setup steps (decision D18: two for now, Preferences and KYC). The avatar's
 * dot shows while one of them still needs the member to act: preferences not
 * set, or no passport uploaded. A passport under review needs nothing from
 * them, so it does not keep the dot on.
 */
export const setupNeedsAction = user => !hasPreferences(user) || kycStateOf(user) === 'none';

const filled = value =>
  Array.isArray(value)
    ? value.length > 0
    : value !== null && value !== undefined && String(value).trim() !== '';

// The profile questions the form requires today (custom user fields).
const PROFILE_QUESTION_KEYS = [
  'parnexusevents',
  'debtrequirements',
  'insurance_partner',
  'companyName',
  'roleposition',
  'your_industry',
];

/**
 * What still needs the member on each account page (the dots in the account
 * nav, Members Area design 1f; user 2026-10-27: "if there are pending items,
 * mark it"). Only what the forms require today. Pure.
 *   profile:       name, date of birth, country and city of business, and
 *                  the profile questions
 *   notifications: a verified email, a phone number, and the deal
 *                  preferences (deal size, areas of focus, regions)
 *   kyc:           no passport uploaded yet
 */
export const accountPendingOf = user => {
  const attributes = user?.attributes || {};
  const profile = attributes.profile || {};
  const publicData = profile.publicData || {};
  const privateData = profile.privateData || {};

  const profilePending =
    !filled(profile.firstName) ||
    !filled(profile.lastName) ||
    !filled(privateData.birthYear) ||
    !filled(privateData.birthMonth) ||
    !filled(privateData.birthDate) ||
    !filled(privateData.country_of_business || publicData.country_of_business) ||
    !filled(publicData.city) ||
    PROFILE_QUESTION_KEYS.some(key => !filled(publicData[key]));

  const notificationsPending =
    attributes.emailVerified === false ||
    !filled(privateData.telephone_contact) ||
    !filled(publicData.transactionsize) ||
    !filled(publicData.mainFocus) ||
    !filled(publicData.region);

  return {
    profile: profilePending,
    notifications: notificationsPending,
    kyc: kycStateOf(user) === 'none',
  };
};

/**
 * The empty-block rule (ACs, shared rules): a block with nothing to show is
 * hidden. Use as `{hasContent(items) ? <Block /> : null}`.
 */
export const hasContent = value =>
  Array.isArray(value) ? value.length > 0 : value !== null && value !== undefined && value !== '';
